import { useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { translateAuthError } from '../../lib/authErrors';

interface MfaChallengeFormProps {
	onSuccess: () => void;
	onCancel?: () => void;
}

/**
 * Chiede il codice a 6 cifre dell'app authenticator e completa la sessione
 * a aal2 (mfa.challenge + mfa.verify) per il primo fattore TOTP registrato.
 */
export default function MfaChallengeForm({ onSuccess, onCancel }: MfaChallengeFormProps) {
	const [code, setCode] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (!/^\d{6}$/.test(code)) {
			setError("Inserisci il codice a 6 cifre dalla tua app authenticator");
			return;
		}

		setSubmitting(true);
		try {
			const { data: factorsData, error: factorsError } =
				await supabase.auth.mfa.listFactors();

			if (factorsError) throw factorsError;

			const totpFactor = factorsData.totp[0];
			if (!totpFactor) {
				setError("Nessun fattore 2FA trovato per questo account");
				return;
			}

			const { data: challengeData, error: challengeError } =
				await supabase.auth.mfa.challenge({ factorId: totpFactor.id });
			if (challengeError) throw challengeError;

			const { error: verifyError } = await supabase.auth.mfa.verify({
				factorId: totpFactor.id,
				challengeId: challengeData.id,
				code,
			});
			if (verifyError) throw verifyError;

			onSuccess();
		} catch (err: any) {
			setError(translateAuthError(err?.message));
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			<div className="flex items-center gap-2 text-foreground/80">
				<ShieldCheck className="h-5 w-5 text-primary" />
				<p className="text-sm">Inserisci il codice di 6 cifre dall&apos;app del telefono (su iPhone te lo propone la tastiera)</p>
			</div>

			{error && (
				<div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3">
					<p className="text-sm text-red-800 dark:text-red-200">{error}</p>
				</div>
			)}

			<Input
				inputMode="numeric"
				autoComplete="one-time-code"
				maxLength={6}
				placeholder="123456"
				value={code}
				onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
				className="text-center text-lg tracking-widest"
				autoFocus
			/>

			<div className="flex gap-2">
				{onCancel && (
					<Button type="button" variant="outline" onClick={onCancel} className="flex-1">
						Annulla
					</Button>
				)}
				<Button type="submit" disabled={submitting} className="flex-1">
					{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verifica"}
				</Button>
			</div>
		</form>
	);
}
